import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { RagChip } from "@/components/RagChip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, Plus, CircleSlash, Loader2, TrendingUp, HeartPulse } from "lucide-react";

const PIE = ["#0078D4", "#008272", "#8764B8", "#D97706", "#A80000", "#498205", "#005A9E"];

export default function Trends() {
  const { session, asOf } = useSession();
  const [monthA, setMonthA] = useState(null);
  const [monthB, setMonthB] = useState(null);

  const { data: trends, isLoading } = useQuery({
    queryKey: ["trends", session?.persona],
    queryFn: async () => (await api.get("/trends")).data,
  });

  useEffect(() => {
    const series = trends?.series || [];
    if (series.length === 0) return;
    const keys = series.map((s) => s.month_key);
    let bIdx = series.length - 1;
    if (asOf !== "latest") {
      const i = keys.indexOf(asOf);
      if (i >= 0) bIdx = i;
    }
    const aIdx = Math.max(0, bIdx - 1);
    setMonthB(keys[bIdx]);
    setMonthA(keys[aIdx]);
  }, [trends, asOf]);

  const { data: cmp } = useQuery({
    queryKey: ["compare", session?.persona, monthA, monthB],
    queryFn: async () => (await api.get("/compare", { params: { month_a: monthA, month_b: monthB } })).data,
    enabled: !!monthA && !!monthB,
  });

  if (isLoading || !trends) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#0078D4]" size={26} /></div>;
  const months = trends.series;

  return (
    <div className="space-y-5 fluent-enter" data-testid="trends-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#201F1E]">Trends &amp; Compare</h1>
        <p className="text-sm text-[#605E5C] mt-1">Portfolio completion and health across months · distributions use one latest snapshot per project ({trends.unique_projects} projects).</p>
      </div>

      {/* Distributions moved here */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="fluent-card p-5 overflow-hidden" data-testid="chart-platform">
          <h3 className="text-sm font-semibold text-[#201F1E] mb-4">Projects by Platform / Product</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trends.platform_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1DFDD" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#605E5C" }} /><YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#605E5C" }} />
              <Tooltip /><Bar dataKey="value" name="Projects" radius={[4, 4, 0, 0]} fill="#0078D4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="fluent-card p-5 overflow-hidden" data-testid="chart-tech">
          <h3 className="text-sm font-semibold text-[#201F1E] mb-4">Distribution by Tech Stack</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={trends.tech_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                {trends.tech_distribution.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 max-h-28 overflow-y-auto pr-1 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="tech-legend">
            {trends.tech_distribution.map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px] text-[#323130] min-w-0" title={`${t.name} (${t.value})`}>
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: PIE[i % PIE.length] }} />
                <span className="truncate max-w-[180px]">{t.name}</span>
                <span className="text-[#605E5C] shrink-0">({t.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="fluent-card p-5" data-testid="trend-completion">
          <h3 className="text-sm font-semibold text-[#201F1E] mb-4">Average % Completion by Month</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1DFDD" vertical={false} />
              <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#605E5C" }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#605E5C" }} />
              <Tooltip /><Line type="monotone" dataKey="avg_completion" name="Avg %" stroke="#0078D4" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="fluent-card p-5" data-testid="trend-health">
          <h3 className="text-sm font-semibold text-[#201F1E] mb-4">Project Health Mix by Month</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1DFDD" vertical={false} />
              <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#605E5C" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#605E5C" }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="green" stackId="a" name="Green" fill="#107C41" />
              <Bar dataKey="amber" stackId="a" name="Amber" fill="#D97706" />
              <Bar dataKey="red" stackId="a" name="Red" fill="#A80000" />
              <Bar dataKey="not_rated" stackId="a" name="Not Rated" fill="#8A8886" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Compare */}
      <div className="fluent-card p-5" data-testid="compare-panel">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h3 className="text-sm font-semibold text-[#201F1E]">Compare two reporting months</h3>
          <div className="flex items-center gap-2">
            <MonthSelect testid="compare-month-a" value={monthA} onChange={setMonthA} months={months} />
            <span className="text-[#605E5C] text-sm">→</span>
            <MonthSelect testid="compare-month-b" value={monthB} onChange={setMonthB} months={months} />
          </div>
        </div>
        {!cmp ? <p className="text-sm text-[#605E5C] mt-3">Pick two months.</p> : (
          <>
            <p className="text-xs text-[#605E5C] mb-4" data-testid="compare-denominators">
              Actual submissions compared by stable project ID. {cmp.reporting_a} reported in the earlier month, {cmp.reporting_b} in the later month.
            </p>

            <div className="mb-5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#201F1E] mb-2"><TrendingUp size={14} /> Completion movement (percentage points)</div>
              <div className="grid gap-3 md:grid-cols-3">
                <Col title="Completion increased" testid="completion-increased" color="#107C41" icon={ArrowUpRight} rows={cmp.completion.increased} render={(r) => `${r.pct_a}% → ${r.pct_b}% (+${r.delta})`} />
                <Col title="Completion decreased" testid="completion-decreased" color="#A80000" icon={ArrowDownRight} rows={cmp.completion.decreased} render={(r) => `${r.pct_a}% → ${r.pct_b}% (${r.delta})`} />
                <Col title="Completion unchanged" testid="completion-unchanged" color="#605E5C" icon={Minus} rows={cmp.completion.unchanged} render={(r) => `${r.pct_a ?? "—"}% → ${r.pct_b ?? "—"}%`} />
              </div>
            </div>

            <div className="mb-5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#201F1E] mb-2"><HeartPulse size={14} /> Project Health movement</div>
              <div className="grid gap-3 md:grid-cols-4">
                <Col title="Health improved" testid="health-improved" color="#107C41" icon={ArrowUpRight} rows={cmp.health.improved} render={null} showHealth />
                <Col title="Health deteriorated" testid="health-deteriorated" color="#A80000" icon={ArrowDownRight} rows={cmp.health.deteriorated} render={null} showHealth />
                <Col title="Health unchanged" testid="health-unchanged" color="#605E5C" icon={Minus} rows={cmp.health.unchanged} render={null} showHealth />
                <Col title="Not comparable" testid="health-not-comparable" color="#8A8886" icon={CircleSlash} rows={cmp.health.not_comparable} render={null} showHealth />
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-[#201F1E] mb-2">Reporting changes</div>
              <div className="grid gap-3 md:grid-cols-2">
                <Col title="Newly reporting in later month" testid="compare-new" color="#0078D4" icon={Plus} rows={cmp.newly_reporting} render={(r) => `${r.pct_b ?? "—"}%`} ragKey="rag_b" />
                <Col title="No submission in later month" testid="compare-no-submission" color="#8764B8" icon={CircleSlash} rows={cmp.no_submission_later} render={(r) => `last: ${r.pct_a ?? "—"}%`} ragKey="rag_a" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MonthSelect({ testid, value, onChange, months }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px] text-sm fluent-focus" data-testid={testid}><SelectValue placeholder="Month" /></SelectTrigger>
      <SelectContent>{months.map((m) => <SelectItem key={m.month_key} value={m.month_key}>{m.month_label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Col({ title, testid, color, icon: Icon, rows, render, ragKey, showHealth }) {
  return (
    <div className="rounded border border-[#E1DFDD] overflow-hidden" data-testid={testid}>
      <div className="px-3 py-2 flex items-center gap-1.5 text-xs font-semibold text-white" style={{ background: color }}>
        <Icon size={14} /> {title} <span className="ml-auto opacity-90">{rows.length}</span>
      </div>
      <div className="divide-y divide-[#EDEBE9] max-h-56 overflow-y-auto">
        {rows.length === 0 ? <div className="px-3 py-4 text-xs text-[#605E5C]">None.</div> :
          rows.map((r, i) => (
            <div key={i} className="px-3 py-2 text-xs">
              <div className="font-semibold text-[#201F1E] truncate">{r.project_name}</div>
              {showHealth ? (
                <div className="flex items-center gap-1.5 mt-1"><RagChip rag={r.rag_a} /><span className="text-[#605E5C]">→</span><RagChip rag={r.rag_b} /></div>
              ) : (
                <div className="flex items-center justify-between mt-1 text-[#605E5C]">
                  <span>{render ? render(r) : ""}</span>
                  {ragKey && r[ragKey] && <RagChip rag={r[ragKey]} />}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
